<template>
  <div class="container py-4">
    <div class="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
      <h2>{{ $t('nav.reports') || 'Reports' }}</h2>
      <button v-if="reportData.results && reportData.results.length" class="btn btn-success" @click="exportReportCSV">
        <i class="bi bi-file-earmark-spreadsheet me-1"></i>{{ $t('dashboard.exportCSV') || 'Export CSV' }}
      </button>
    </div>

    <!-- Report Type Selector & Filter Row -->
    <div class="card p-3 mb-4 border-0 shadow-sm bg-body-tertiary">
      <div class="row g-3">
        <!-- Report Type -->
        <div class="col-md-3">
          <label class="form-label fw-bold">Report Type</label>
          <select v-model="selectedType" class="form-select" @change="onReportTypeChange">
            <option value="stock_valuation">Inventory Valuation</option>
            <option value="stock_movement">Stock Movements Activity</option>
            <option value="sales">Sales Performance</option>
            <option value="purchases">Purchases Summary</option>
          </select>
        </div>

        <!-- Date Filters (only for date-sensitive reports) -->
        <div v-if="selectedType !== 'stock_valuation'" class="col-md-4 d-flex gap-2">
          <div class="flex-grow-1">
            <label class="form-label fw-bold">Start Date</label>
            <input v-model="filters.start_date" type="date" class="form-control" />
          </div>
          <div class="flex-grow-1">
            <label class="form-label fw-bold">End Date</label>
            <input v-model="filters.end_date" type="date" class="form-control" />
          </div>
        </div>

        <!-- Category -->
        <div class="col-md-2">
          <label class="form-label fw-bold">{{ $t('product.category') || 'Category' }}</label>
          <select v-model="filters.category" class="form-select">
            <option value="">All Categories</option>
            <option v-for="c in dropdowns.categories" :key="c.id" :value="c.id">{{ c.name }}</option>
          </select>
        </div>

        <!-- Warehouse -->
        <div class="col-md-2">
          <label class="form-label fw-bold">{{ $t('product.warehouse') || 'Warehouse' }}</label>
          <select v-model="filters.warehouse" class="form-select">
            <option value="">All Warehouses</option>
            <option v-for="w in dropdowns.warehouses" :key="w.id" :value="w.id">{{ w.name }}</option>
          </select>
        </div>
      </div>

      <div class="row g-3 mt-2">
        <!-- Product -->
        <div class="col-md-3">
          <label class="form-label fw-bold">{{ $t('stock.product') || 'Product' }}</label>
          <select v-model="filters.product" class="form-select">
            <option value="">All Products</option>
            <option v-for="p in dropdowns.products" :key="p.id" :value="p.id">{{ p.name }} ({{ p.sku }})</option>
          </select>
        </div>

        <!-- Supplier -->
        <div class="col-md-3">
          <label class="form-label fw-bold">{{ $t('purchaseOrder.supplier') || 'Supplier' }}</label>
          <select v-model="filters.supplier" class="form-select">
            <option value="">All Suppliers</option>
            <option v-for="s in dropdowns.suppliers" :key="s.id" :value="s.id">{{ s.name }}</option>
          </select>
        </div>

        <!-- Customer (only for Sales report) -->
        <div v-if="selectedType === 'sales'" class="col-md-3">
          <label class="form-label fw-bold">{{ $t('salesOrder.customer') || 'Customer' }}</label>
          <select v-model="filters.customer" class="form-select">
            <option value="">All Customers</option>
            <option v-for="c in dropdowns.customers" :key="c.id" :value="c.id">{{ c.name }}</option>
          </select>
        </div>

        <!-- Submit Button -->
        <div class="col-md-3 d-flex align-items-end">
          <button class="btn btn-primary w-100" @click="fetchReport">
            <i class="bi bi-search me-1"></i>Generate Report
          </button>
        </div>
      </div>
    </div>

    <!-- Summary Statistics / KPI Row -->
    <div v-if="reportData.summary" class="row g-3 mb-4">
      <!-- Valuation Summary -->
      <template v-if="selectedType === 'stock_valuation'">
        <div class="col-md-6">
          <div class="card text-white bg-primary shadow-sm border-0">
            <div class="card-body">
              <h6 class="card-subtitle mb-1 opacity-75">Total Quantity on Hand</h6>
              <h3>{{ reportData.summary.total_quantity }} Units</h3>
            </div>
          </div>
        </div>
        <div class="col-md-6">
          <div class="card text-white bg-success shadow-sm border-0">
            <div class="card-body">
              <h6 class="card-subtitle mb-1 opacity-75">Total Inventory Valuation (FIFO)</h6>
              <h3>{{ currency(reportData.summary.total_value) }}</h3>
            </div>
          </div>
        </div>
      </template>

      <!-- Movements Summary -->
      <template v-else-if="selectedType === 'stock_movement'">
        <div class="col-md-4">
          <div class="card text-white bg-success shadow-sm border-0">
            <div class="card-body">
              <h6 class="card-subtitle mb-1 opacity-75">Total Received (IN)</h6>
              <h3>{{ reportData.summary.total_in_qty }} Units <small class="fs-6 opacity-75">({{ currency(reportData.summary.total_in_value) }})</small></h3>
            </div>
          </div>
        </div>
        <div class="col-md-4">
          <div class="card text-white bg-danger shadow-sm border-0">
            <div class="card-body">
              <h6 class="card-subtitle mb-1 opacity-75">Total Shipped (OUT)</h6>
              <h3>{{ reportData.summary.total_out_qty }} Units <small class="fs-6 opacity-75">({{ currency(reportData.summary.total_out_value) }})</small></h3>
            </div>
          </div>
        </div>
        <div class="col-md-4">
          <div class="card text-white bg-secondary shadow-sm border-0">
            <div class="card-body">
              <h6 class="card-subtitle mb-1 opacity-75">Total Adjusted</h6>
              <h3>{{ reportData.summary.total_adj_qty }} Units <small class="fs-6 opacity-75">({{ currency(reportData.summary.total_adj_value) }})</small></h3>
            </div>
          </div>
        </div>
      </template>

      <!-- Sales Summary -->
      <template v-else-if="selectedType === 'sales'">
        <div class="col-md-3">
          <div class="card text-white bg-primary shadow-sm border-0">
            <div class="card-body py-3">
              <h6 class="card-subtitle mb-1 opacity-75">Total Revenue</h6>
              <h4>{{ currency(reportData.summary.total_revenue) }}</h4>
            </div>
          </div>
        </div>
        <div class="col-md-3">
          <div class="card text-white bg-secondary shadow-sm border-0">
            <div class="card-body py-3">
              <h6 class="card-subtitle mb-1 opacity-75">Total Cost (COGS)</h6>
              <h4>{{ currency(reportData.summary.total_cogs) }}</h4>
            </div>
          </div>
        </div>
        <div class="col-md-3">
          <div class="card text-white bg-success shadow-sm border-0">
            <div class="card-body py-3">
              <h6 class="card-subtitle mb-1 opacity-75">Gross Profit</h6>
              <h4>{{ currency(reportData.summary.total_profit) }}</h4>
            </div>
          </div>
        </div>
        <div class="col-md-3">
          <div class="card text-white bg-info shadow-sm border-0">
            <div class="card-body py-3">
              <h6 class="card-subtitle mb-1 opacity-75">Avg Gross Margin</h6>
              <h4>{{ reportData.summary.avg_margin_pct.toFixed(2) }}%</h4>
            </div>
          </div>
        </div>
      </template>

      <!-- Purchases Summary -->
      <template v-else-if="selectedType === 'purchases'">
        <div class="col-md-6">
          <div class="card text-white bg-primary shadow-sm border-0">
            <div class="card-body">
              <h6 class="card-subtitle mb-1 opacity-75">Total Units Purchased</h6>
              <h3>{{ reportData.summary.total_quantity }} Units</h3>
            </div>
          </div>
        </div>
        <div class="col-md-6">
          <div class="card text-white bg-success shadow-sm border-0">
            <div class="card-body">
              <h6 class="card-subtitle mb-1 opacity-75">Total Purchase Value</h6>
              <h3>{{ currency(reportData.summary.total_cost) }}</h3>
            </div>
          </div>
        </div>
      </template>
    </div>

    <!-- Report Data Table -->
    <div class="card shadow-sm border-0">
      <div class="card-body p-0">
        <div v-if="loading" class="text-center p-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <p class="mt-2 text-muted">Generating report data...</p>
        </div>

        <div v-else-if="!reportData.results || !reportData.results.length" class="text-center p-5 text-muted">
          <i class="bi bi-file-earmark-text fs-1"></i>
          <p class="mt-2">No matching records found. Modify your filters and click "Generate Report".</p>
        </div>

        <div v-else class="table-responsive">
          <!-- Valuation Table -->
          <table v-if="selectedType === 'stock_valuation'" class="table table-hover table-striped mb-0">
            <thead class="table-dark">
              <tr>
                <th>{{ $t('product.sku') || 'SKU' }}</th>
                <th>{{ $t('stock.product') || 'Product' }}</th>
                <th>{{ $t('product.category') || 'Category' }}</th>
                <th>{{ $t('product.warehouse') || 'Warehouse' }}</th>
                <th class="text-end">Quantity</th>
                <th class="text-end">Avg Cost/Unit</th>
                <th class="text-end">FIFO Total Value</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in reportData.results" :key="`${row.product_id}-${row.warehouse_name}`">
                <td>{{ row.sku }}</td>
                <td>{{ row.product_name }}</td>
                <td>{{ row.category_name }}</td>
                <td>{{ row.warehouse_name }}</td>
                <td class="text-end">{{ row.quantity }}</td>
                <td class="text-end">{{ currency(row.unit_cost) }}</td>
                <td class="text-end fw-bold">{{ currency(row.total_value) }}</td>
              </tr>
            </tbody>
          </table>

          <!-- Movements Table -->
          <table v-else-if="selectedType === 'stock_movement'" class="table table-hover table-striped mb-0 table-sm">
            <thead class="table-dark">
              <tr>
                <th>{{ $t('stock.date') || 'Date' }}</th>
                <th>{{ $t('stock.product') || 'Product' }}</th>
                <th>{{ $t('product.category') || 'Category' }}</th>
                <th>{{ $t('product.warehouse') || 'Warehouse' }}</th>
                <th>{{ $t('stock.type') || 'Type' }}</th>
                <th>{{ $t('stock.reason') || 'Reference' }}</th>
                <th class="text-end">Quantity</th>
                <th class="text-end">Unit Cost</th>
                <th class="text-end">Total Value</th>
                <th class="text-end">Balance After</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in reportData.results" :key="row.id">
                <td>{{ new Date(row.created_at).toLocaleString() }}</td>
                <td>{{ row.product_name }} <small class="text-muted">({{ row.sku }})</small></td>
                <td>{{ row.category_name }}</td>
                <td>{{ row.warehouse_name }}</td>
                <td>
                  <span class="badge" :class="row.transaction_type === 'IN' ? 'bg-success' : (row.transaction_type === 'OUT' ? 'bg-danger' : 'bg-warning')">
                    {{ row.transaction_type }}
                  </span>
                </td>
                <td>{{ row.reference }}</td>
                <td class="text-end">{{ row.quantity }}</td>
                <td class="text-end">{{ currency(row.unit_cost) }}</td>
                <td class="text-end">{{ currency(row.total_value) }}</td>
                <td class="text-end">{{ row.balance_after }}</td>
              </tr>
            </tbody>
          </table>

          <!-- Sales Table -->
          <table v-else-if="selectedType === 'sales'" class="table table-hover table-striped mb-0">
            <thead class="table-dark">
              <tr>
                <th>Ship Date</th>
                <th>Reference</th>
                <th>{{ $t('salesOrder.customer') || 'Customer' }}</th>
                <th>{{ $t('stock.product') || 'Product' }}</th>
                <th class="text-end">Qty Shipped</th>
                <th class="text-end">Unit Price</th>
                <th class="text-end">Revenue</th>
                <th class="text-end">Cost (COGS)</th>
                <th class="text-end">Gross Profit</th>
                <th class="text-end">Margin %</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, idx) in reportData.results" :key="idx">
                <td>{{ row.shipped_at ? new Date(row.shipped_at).toLocaleDateString() : '—' }}</td>
                <td>{{ row.reference }}</td>
                <td>{{ row.customer_name }}</td>
                <td>{{ row.product_name }} <small class="text-muted">({{ row.sku }})</small></td>
                <td class="text-end">{{ row.quantity }}</td>
                <td class="text-end">{{ currency(row.unit_price) }}</td>
                <td class="text-end">{{ currency(row.revenue) }}</td>
                <td class="text-end text-muted">{{ currency(row.cogs) }}</td>
                <td class="text-end" :class="row.profit >= 0 ? 'text-success fw-bold' : 'text-danger fw-bold'">
                  {{ currency(row.profit) }}
                </td>
                <td class="text-end">{{ row.margin_pct.toFixed(1) }}%</td>
              </tr>
            </tbody>
          </table>

          <!-- Purchases Table -->
          <table v-else-if="selectedType === 'purchases'" class="table table-hover table-striped mb-0">
            <thead class="table-dark">
              <tr>
                <th>Receive Date</th>
                <th>Reference</th>
                <th>{{ $t('purchaseOrder.supplier') || 'Supplier' }}</th>
                <th>{{ $t('stock.product') || 'Product' }}</th>
                <th>{{ $t('product.warehouse') || 'Warehouse' }}</th>
                <th class="text-end">Qty Received</th>
                <th class="text-end">Unit Cost</th>
                <th class="text-end">Total Cost</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, idx) in reportData.results" :key="idx">
                <td>{{ row.received_at ? new Date(row.received_at).toLocaleDateString() : '—' }}</td>
                <td>{{ row.reference }}</td>
                <td>{{ row.supplier_name }}</td>
                <td>{{ row.product_name }} <small class="text-muted">({{ row.sku }})</small></td>
                <td>{{ row.warehouse_name }}</td>
                <td class="text-end">{{ row.quantity }}</td>
                <td class="text-end">{{ currency(row.unit_cost) }}</td>
                <td class="text-end fw-bold">{{ currency(row.total_cost) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import api from '../api/axios'

const selectedType = ref('stock_valuation')
const loading = ref(false)

const filters = ref({
  start_date: '',
  end_date: '',
  category: '',
  warehouse: '',
  product: '',
  supplier: '',
  customer: ''
})

const dropdowns = ref({
  categories: [],
  warehouses: [],
  products: [],
  suppliers: [],
  customers: []
})

const reportData = ref({
  results: [],
  summary: null
})

const currency = (v) => new Intl.NumberFormat('en-ET', { style: 'currency', currency: 'ETB' }).format(v)

const onReportTypeChange = () => {
  reportData.value = { results: [], summary: null }
}

const fetchDropdowns = async () => {
  try {
    const [catRes, whRes, prodRes, supRes, custRes] = await Promise.all([
      api.get('categories/?pagination=false'),
      api.get('warehouses/?pagination=false'),
      api.get('products/?pagination=false'),
      api.get('suppliers/?pagination=false'),
      api.get('customers/?pagination=false')
    ])
    dropdowns.value.categories = catRes.data
    dropdowns.value.warehouses = whRes.data
    dropdowns.value.products = prodRes.data
    dropdowns.value.suppliers = supRes.data
    dropdowns.value.customers = custRes.data
  } catch (err) {
    console.error('Error fetching dropdown filters:', err)
  }
}

const fetchReport = async () => {
  loading.value = true
  try {
    const activeFilters = { ...filters.value }
    // Clean up empty filters
    Object.keys(activeFilters).forEach(key => {
      if (activeFilters[key] === '') {
        delete activeFilters[key]
      }
    })
    
    const res = await api.get(`/reports/${selectedType.value}/`, {
      params: activeFilters
    })
    reportData.value = res.data
  } catch (err) {
    console.error('Failed to load report data:', err)
    alert('Failed to generate report.')
  } finally {
    loading.value = false
  }
}

const exportReportCSV = () => {
  const arr = reportData.value.results
  if (!arr.length) return
  
  const header = Object.keys(arr[0]).join(',')
  const rows = arr.map(r => Object.values(r).map(val => {
    if (typeof val === 'string' && val.includes(',')) {
      return `"${val.replace(/"/g, '""')}"`
    }
    return val
  }).join(','))
  
  const blob = new Blob([[header, ...rows].join('\n')], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${selectedType.value}-report.csv`
  a.click()
  URL.revokeObjectURL(url)
}

onMounted(() => {
  fetchDropdowns()
})
</script>

<style scoped>
.card {
  border-radius: 8px;
}
.btn-xs {
  padding: 0.15rem 0.4rem;
  font-size: 0.75rem;
}
</style>
